import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs from "dayjs";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Grid,
  Group,
  Pagination,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBell,
  IconCalendarDue,
  IconClipboardCheck,
  IconDownload,
  IconFileSearch,
  IconMail,
  IconPhone,
  IconSearch,
  IconShieldCheck,
  IconUserPlus,
} from "@tabler/icons-react";
import { publicTenderApi, type PublicTender } from "../api/publicTenders";
import { Logo } from "../components/Logo";
import { BRAND_DARK, BRAND_MEDIUM } from "../theme/brand";
import { useVendorAuth } from "../modules/vendors/auth/VendorAuthContext";

const PAGE_SIZE = 9;

const CATEGORY_OPTIONS = [
  { value: "", label: "All" },
  { value: "GOODS", label: "Goods" },
  { value: "SERVICES", label: "Services" },
  { value: "WORKS", label: "Works" },
];

const CATEGORY_LABELS: Record<string, string> = { GOODS: "Goods", SERVICES: "Services", WORKS: "Works" };

const STEPS = [
  {
    icon: IconUserPlus,
    title: "Register your company",
    body: "Create a vendor account once and upload your business registration and supporting documents.",
  },
  {
    icon: IconFileSearch,
    title: "Find an opportunity",
    body: "Browse open tenders, read the requirements and download the terms of reference (TOR).",
  },
  {
    icon: IconClipboardCheck,
    title: "Submit and track your bid",
    body: "Submit before the deadline, then follow your bid's progress from your vendor portal.",
  },
];

/** Deadline wording with text (not colour alone) so urgency is clear to everyone. */
function deadlineInfo(deadline: string): { label: string; color: string; open: boolean } {
  const days = dayjs(deadline).startOf("day").diff(dayjs().startOf("day"), "day");
  if (dayjs().isAfter(dayjs(deadline))) return { label: "Closed", color: "gray", open: false };
  if (days === 0) return { label: "Closes today", color: "red", open: true };
  if (days === 1) return { label: "Closes tomorrow", color: "red", open: true };
  if (days <= 7) return { label: `Closes in ${days} days`, color: "orange", open: true };
  return { label: `Closes in ${days} days`, color: BRAND_MEDIUM, open: true };
}

function TenderCard({ tender }: { tender: PublicTender }) {
  const [downloading, setDownloading] = useState(false);
  const deadline = deadlineInfo(tender.submissionDeadline);

  const downloadTor = async () => {
    setDownloading(true);
    try {
      await publicTenderApi.downloadTorDocument(tender.id);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card withBorder radius="md" padding="lg" h="100%" className="transition-shadow duration-200 hover:shadow-md">
      <Stack gap="sm" h="100%">
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Badge variant="light" color="gray" radius="sm">
            {CATEGORY_LABELS[tender.category] ?? tender.category}
          </Badge>
          <Badge variant="light" color={deadline.color} radius="sm">
            {deadline.label}
          </Badge>
        </Group>

        <Stack gap={4} style={{ flex: 1 }}>
          <Text size="xs" c="dimmed" fw={500}>
            {tender.referenceNo}
          </Text>
          <Title order={3} size="h5" lh={1.4} lineClamp={3}>
            {tender.description}
          </Title>
        </Stack>

        <Stack gap={4}>
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Published
            </Text>
            <Text size="xs" fw={500}>
              {dayjs(tender.publishingDate).format("D MMM YYYY")}
            </Text>
          </Group>
          <Group justify="space-between">
            <Text size="xs" c="dimmed">
              Submission deadline
            </Text>
            <Text size="xs" fw={600} c={deadline.open ? undefined : "dimmed"}>
              {dayjs(tender.submissionDeadline).format("D MMM YYYY, HH:mm")}
            </Text>
          </Group>
          {tender.requirePrebidMeeting && tender.prebidMeetingDate && (
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                Pre-bid meeting
              </Text>
              <Text size="xs" fw={500}>
                {dayjs(tender.prebidMeetingDate).format("D MMM YYYY, HH:mm")}
              </Text>
            </Group>
          )}
        </Stack>

        <Group gap="xs" mt="xs" grow>
          <Button
            component={Link}
            to={`/public/tenders/${tender.id}`}
            color={BRAND_MEDIUM}
            size="sm"
            rightSection={<IconArrowRight size={14} />}
            aria-label={`View details for ${tender.referenceNo}`}
          >
            View details
          </Button>
          {tender.torDocument && (
            <Button
              variant="default"
              size="sm"
              leftSection={<IconDownload size={14} />}
              onClick={downloadTor}
              loading={downloading}
              aria-label={`Download terms of reference for ${tender.referenceNo}`}
            >
              TOR
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

export const VendorLanding: React.FC = () => {
  const { vendor } = useVendorAuth();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);

  const { data: tenders, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["public-tenders", debouncedSearch, category, page],
    queryFn: () =>
      publicTenderApi.getPublishedTenders({
        search: debouncedSearch || undefined,
        category: category || undefined,
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  // Unfiltered snapshot for the hero figures, so they don't change while searching.
  const { data: overview } = useQuery({
    queryKey: ["public-tenders-overview"],
    queryFn: () => publicTenderApi.getPublishedTenders({ page: 1, limit: 50 }),
  });
  const openNow = (overview?.data ?? []).filter((t) => deadlineInfo(t.submissionDeadline).open);
  const nextDeadline = [...openNow].sort(
    (a, b) => dayjs(a.submissionDeadline).valueOf() - dayjs(b.submissionDeadline).valueOf()
  )[0];

  const filtered = !!debouncedSearch || !!category;

  return (
    <div className="min-h-screen bg-[var(--mantine-color-body)]">
      <a
        href="#tenders"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:shadow"
      >
        Skip to tenders
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-body)]">
        <Container size="lg" py="sm">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Logo height={40} />
              <Text fw={700} size="lg" c={BRAND_DARK} visibleFrom="xs">
                HMH Vendor Portal
              </Text>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Button component="a" href="#tenders" variant="subtle" color="gray" visibleFrom="sm">
                Tenders
              </Button>
              {vendor ? (
                <Button component={Link} to="/vendor/dashboard" color={BRAND_MEDIUM} rightSection={<IconArrowRight size={16} />}>
                  My portal
                </Button>
              ) : (
                <>
                  <Button component={Link} to="/vendor/login" variant="subtle" color="dark">
                    Sign in
                  </Button>
                  <Button component={Link} to="/vendor/register" color={BRAND_MEDIUM} visibleFrom="xs">
                    Register
                  </Button>
                </>
              )}
            </Group>
          </Group>
        </Container>
      </header>

      <main>
        {/* Hero */}
        <section style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND_MEDIUM} 100%)` }}>
          <Container size="lg" py={{ base: 48, md: 80 }}>
            <Grid gutter={{ base: 32, md: 64 }} align="center">
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Stack gap="lg">
                  <Text size="sm" fw={600} c="rgba(255,255,255,0.85)" tt="uppercase" lts={1}>
                    Hulhumalé Hospital · Supplier opportunities
                  </Text>
                  <Title order={1} c="white" fz={{ base: 32, md: 46 }} lh={1.15}>
                    Supply Hulhumalé Hospital
                  </Title>
                  <Text size="lg" c="rgba(255,255,255,0.9)" maw={540}>
                    Find open tenders, download tender documents and submit bids online. Register once and use the
                    same account for every opportunity.
                  </Text>
                  <Group gap="sm">
                    {vendor ? (
                      <Button
                        component={Link}
                        to="/vendor/dashboard"
                        size="md"
                        color="white"
                        c={BRAND_DARK}
                        rightSection={<IconArrowRight size={18} />}
                      >
                        Go to my portal
                      </Button>
                    ) : (
                      <>
                        <Button
                          component={Link}
                          to="/vendor/register"
                          size="md"
                          color="white"
                          c={BRAND_DARK}
                          rightSection={<IconArrowRight size={18} />}
                        >
                          Register as a vendor
                        </Button>
                        <Button
                          component={Link}
                          to="/vendor/login"
                          size="md"
                          variant="outline"
                          color="white"
                        >
                          Sign in
                        </Button>
                      </>
                    )}
                  </Group>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 5 }}>
                <SimpleGrid cols={2} spacing="md">
                  <Card radius="md" padding="lg" shadow="md">
                    <Text size="sm" c="dimmed">
                      Open for bids
                    </Text>
                    <Text fz={36} fw={700} c={BRAND_DARK} style={{ fontVariantNumeric: "tabular-nums" }}>
                      {overview ? openNow.length : "–"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      of {overview?.meta.total ?? "–"} published
                    </Text>
                  </Card>
                  <Card radius="md" padding="lg" shadow="md">
                    <Text size="sm" c="dimmed">
                      Next deadline
                    </Text>
                    <Text fz={36} fw={700} c={BRAND_DARK}>
                      {nextDeadline ? dayjs(nextDeadline.submissionDeadline).format("D MMM") : "–"}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {nextDeadline ? nextDeadline.referenceNo : "No open tenders"}
                    </Text>
                  </Card>
                  <Card radius="md" padding="lg" shadow="md" style={{ gridColumn: "1 / -1" }}>
                    <Group gap="sm" wrap="nowrap">
                      <ThemeIcon variant="light" color={BRAND_MEDIUM} radius="md" size={36}>
                        <IconShieldCheck size={20} aria-hidden />
                      </ThemeIcon>
                      <Text size="sm">
                        Every tender is evaluated by the hospital's bid committee against the published criteria.
                      </Text>
                    </Group>
                  </Card>
                </SimpleGrid>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        {/* Tender search */}
        <section id="tenders" aria-labelledby="tenders-heading" style={{ scrollMarginTop: 72 }}>
          <Container size="lg" py={{ base: 48, md: 72 }}>
            <Stack gap={4} mb="lg">
              <Title id="tenders-heading" order={2}>
                Tender opportunities
              </Title>
              <Text c="dimmed">Open a tender for its full requirements, items and submission instructions.</Text>
            </Stack>

            <Group gap="md" mb="lg" align="flex-end" wrap="wrap">
              <TextInput
                label="Search tenders"
                placeholder="Reference number or description"
                leftSection={<IconSearch size={16} aria-hidden />}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  setPage(1);
                }}
                style={{ flex: "1 1 280px" }}
              />
              <Stack gap={4}>
                <Text size="sm" fw={500} id="category-label">
                  Category
                </Text>
                <SegmentedControl
                  aria-labelledby="category-label"
                  data={CATEGORY_OPTIONS}
                  value={category}
                  onChange={(v) => {
                    setCategory(v);
                    setPage(1);
                  }}
                />
              </Stack>
            </Group>

            <Text size="sm" c="dimmed" mb="md" aria-live="polite">
              {tenders
                ? `${tenders.meta.total} ${tenders.meta.total === 1 ? "tender" : "tenders"}${filtered ? " match your search" : ""}`
                : " "}
            </Text>

            {isLoading ? (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" aria-busy="true" aria-label="Loading tenders">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} h={260} radius="md" />
                ))}
              </SimpleGrid>
            ) : isError ? (
              <Alert color="red" title="Couldn't load tenders">
                <Group gap="sm">
                  <Text size="sm">Please check your connection and try again.</Text>
                  <Button size="xs" variant="light" color="red" onClick={() => refetch()}>
                    Retry
                  </Button>
                </Group>
              </Alert>
            ) : tenders?.data.length ? (
              <>
                <SimpleGrid
                  cols={{ base: 1, sm: 2, lg: 3 }}
                  spacing="lg"
                  style={{ opacity: isFetching ? 0.6 : 1, transition: "opacity 150ms" }}
                >
                  {tenders.data.map((tender) => (
                    <TenderCard key={tender.id} tender={tender} />
                  ))}
                </SimpleGrid>
                {tenders.meta.totalPages > 1 && (
                  <Center mt="xl">
                    <Pagination
                      total={tenders.meta.totalPages}
                      value={page}
                      onChange={setPage}
                      color={BRAND_MEDIUM}
                      getControlProps={(control) => ({ "aria-label": `${control} page` })}
                    />
                  </Center>
                )}
              </>
            ) : (
              <Card withBorder radius="md" padding="xl">
                <Stack align="center" gap="xs" py="lg">
                  <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                    {filtered ? <IconSearch size={26} aria-hidden /> : <IconCalendarDue size={26} aria-hidden />}
                  </ThemeIcon>
                  <Title order={3} size="h5">
                    {filtered ? "No tenders match your search" : "No tenders are open right now"}
                  </Title>
                  <Text size="sm" c="dimmed" ta="center" maw={420}>
                    {filtered
                      ? "Try a different reference number or keyword, or clear the category filter."
                      : "New opportunities are published here. Register now so you're ready to bid when they open."}
                  </Text>
                  {filtered ? (
                    <Button
                      variant="light"
                      color={BRAND_MEDIUM}
                      mt="xs"
                      onClick={() => {
                        setSearch("");
                        setCategory("");
                        setPage(1);
                      }}
                    >
                      Clear search
                    </Button>
                  ) : (
                    !vendor && (
                      <Button component={Link} to="/vendor/register" variant="light" color={BRAND_MEDIUM} mt="xs">
                        Register as a vendor
                      </Button>
                    )
                  )}
                </Stack>
              </Card>
            )}
          </Container>
        </section>

        {/* How it works */}
        <section aria-labelledby="steps-heading" className="bg-[var(--mantine-color-gray-0)]">
          <Container size="lg" py={{ base: 48, md: 72 }}>
            <Title id="steps-heading" order={2} mb={4}>
              How to bid
            </Title>
            <Text c="dimmed" mb="xl">
              Three steps from registration to submission.
            </Text>
            <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
              {STEPS.map((step, i) => (
                <Card key={step.title} withBorder radius="md" padding="lg">
                  <Group gap="sm" mb="sm" wrap="nowrap">
                    <ThemeIcon size={40} radius="md" variant="light" color={BRAND_MEDIUM}>
                      <step.icon size={22} aria-hidden />
                    </ThemeIcon>
                    <Text size="sm" fw={600} c="dimmed">
                      Step {i + 1}
                    </Text>
                  </Group>
                  <Title order={3} size="h5" mb={6}>
                    {step.title}
                  </Title>
                  <Text size="sm" c="dimmed">
                    {step.body}
                  </Text>
                </Card>
              ))}
            </SimpleGrid>
            {!vendor && (
              <Card radius="md" padding="lg" mt="xl" style={{ background: BRAND_DARK }}>
                <Group justify="space-between" gap="md">
                  <Group gap="sm" wrap="nowrap">
                    <ThemeIcon variant="white" color={BRAND_DARK} radius="md" size={40}>
                      <IconBell size={20} aria-hidden />
                    </ThemeIcon>
                    <Stack gap={0}>
                      <Text fw={600} c="white">
                        Ready to work with us?
                      </Text>
                      <Text size="sm" c="rgba(255,255,255,0.85)">
                        Registration takes a few minutes. You'll need your business registration details.
                      </Text>
                    </Stack>
                  </Group>
                  <Button component={Link} to="/vendor/register" color="white" c={BRAND_DARK}>
                    Register now
                  </Button>
                </Group>
              </Card>
            )}
          </Container>
        </section>
      </main>

      {/* Footer */}
      <footer style={{ background: BRAND_DARK }}>
        <Container size="lg" py={40}>
          <Grid gutter="xl">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="sm">
                <Text fw={700} size="lg" c="white">
                  HMH Vendor Portal
                </Text>
                <Text size="sm" c="rgba(255,255,255,0.8)" maw={380}>
                  Vendor registration and bid submission portal for Hulhumalé Hospital procurement.
                </Text>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="xs">
                <Text fw={600} c="white">
                  Vendor support
                </Text>
                <Group gap={6} wrap="nowrap">
                  <IconMail size={16} color="rgba(255,255,255,0.8)" aria-hidden />
                  <Anchor href="mailto:vendors@hmh.mv" size="sm" c="rgba(255,255,255,0.85)">
                    vendors@hmh.mv
                  </Anchor>
                </Group>
                <Group gap={6} wrap="nowrap">
                  <IconPhone size={16} color="rgba(255,255,255,0.8)" aria-hidden />
                  <Anchor href="tel:+9601234567" size="sm" c="rgba(255,255,255,0.85)">
                    +960 123-4567
                  </Anchor>
                </Group>
                <Text size="sm" c="rgba(255,255,255,0.85)">
                  8AM – 5PM, Sunday – Thursday
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>
          <Text
            size="xs"
            c="rgba(255,255,255,0.7)"
            mt="xl"
            pt="lg"
            style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}
          >
            © {new Date().getFullYear()} Hulhumalé Hospital. All rights reserved.
          </Text>
        </Container>
      </footer>
    </div>
  );
};
